import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Langs, { Localize } from '../../app/lang/langs';
import { InfoTooltip } from './PermissionScopeTooltip';
import pStyles from './Permissions.module.css';


/** "Is manager" label + info bubble, shared by the modal and the profiles table header. */
export function IsManagerLabel() {
    return (
        <InfoTooltip
            heading={Localize('IS_MANAGER')}
            description={Localize('IS_MANAGER_DESC')}
            footer={Localize('IS_MANAGER_FOOTER')}
        >
            <span tabIndex={0} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'help' }}>
                <Langs str="IS_MANAGER" />
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255, 144, 21, 0.8)' }} />
            </span>
        </InfoTooltip>
    )
}

// 16px keeps iOS Safari from zooming the whole page the moment a field is focused,
// which on a full-screen dialog leaves the user stranded off-canvas.
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: 'rgba(0, 40, 128, 0.02)',
        '& fieldset': { borderColor: 'rgba(0, 40, 128, 0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(0, 40, 128, 0.25)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255, 144, 21, 0.5)', borderWidth: '1.5px' },
    },
    '& .MuiInputBase-input': { fontSize: '16px' },
}

/** Label above the control on a phone, beside it from `md` up (the desktop shape). */
function FormRow({ label, children }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '170px 1fr' },
                alignItems: { xs: 'stretch', md: 'start' },
                columnGap: 2,
                rowGap: 1,
                py: 1.25,
            }}
        >
            <Box
                sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.95rem', md: '0.9rem' },
                    color: 'rgb(0, 40, 128)',
                    letterSpacing: '-0.01em',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: { md: 40 },
                }}
            >
                {label}
            </Box>
            <Box sx={{ minWidth: 0 }}>{children}</Box>
        </Box>
    )
}


export default function ProfileModal({ visible, onHide, isAddMode, editingProfile, onSave, teamOptions, profileOptions }) {
    let isMobile = useSelector((state) => state.settings.isMobile)
    let isPhone = isMobile === 2

    let [formName, setFormName] = useState('')
    let [formDescription, setFormDescription] = useState('')
    let [formActive, setFormActive] = useState(true)
    let [formDisplayable, setFormDisplayable] = useState(true)
    let [formIsManager, setFormIsManager] = useState(false)
    let [formTeamIds, setFormTeamIds] = useState([])
    let [inheritFromProfileId, setInheritFromProfileId] = useState(null)

    useEffect(() => {
        if (visible) {
            if (isAddMode) {
                setFormName('')
                setFormDescription('')
                setFormActive(true)
                setFormDisplayable(true)
                setFormIsManager(false)
                setFormTeamIds([])
                setInheritFromProfileId(null)
            } else if (editingProfile) {
                setFormName(editingProfile.name)
                setFormDescription(editingProfile.description || '')
                setFormActive(!!editingProfile.active)
                setFormDisplayable(editingProfile.displayable === undefined ? true : !!editingProfile.displayable)
                setFormIsManager(!!editingProfile.is_manager)
                setFormTeamIds(editingProfile.teams ? editingProfile.teams.map(t => t.id) : [])
            }
        }
    }, [visible, isAddMode, editingProfile])

    let handleSave = () => {
        if (!formName.trim()) return
        onSave({
            name: formName.trim(),
            description: formDescription.trim() || null,
            active: formActive,
            displayable: formDisplayable,
            isManager: formIsManager,
            teamIds: formTeamIds,
            inheritFromProfileId: isAddMode ? inheritFromProfileId : null
        })
    }

    let selectedTeams = (teamOptions || []).filter(t => formTeamIds.includes(t.value))
    let selectedInherit = (profileOptions || []).find(p => p.id === inheritFromProfileId) || null

    // A switch on its own is a 38px target; pairing it with a clickable label
    // gives the row a comfortable tap area on a phone.
    let switchRow = (checked, onChange, label) => (
        <FormControlLabel
            control={
                <Switch
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF9015' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FF9015' },
                    }}
                />
            }
            label={
                <Typography sx={{ fontSize: '0.9rem', color: 'rgba(0, 40, 128, 0.75)' }}>
                    {label}
                </Typography>
            }
            sx={{ ml: 0, minHeight: 48, mr: 0 }}
        />
    )

    return (
        <Dialog
            open={!!visible}
            onClose={onHide}
            fullScreen={isPhone}
            fullWidth
            maxWidth="sm"
            aria-labelledby="profile-modal-title"
            PaperProps={{
                sx: {
                    borderRadius: isPhone ? 0 : '16px',
                    border: '1px solid rgba(0, 40, 128, 0.06)',
                },
            }}
        >
            <DialogTitle
                id="profile-modal-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    background: 'linear-gradient(135deg, #fff5eb, #f0f2f8)',
                    borderBottom: '1px solid rgba(0, 40, 128, 0.1)',
                    px: 2.5,
                    py: 2,
                    pt: isPhone ? 'calc(env(safe-area-inset-top, 0px) + 16px)' : 2,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: 'rgb(0, 40, 128)',
                    letterSpacing: '-0.02em',
                }}
            >
                <Langs str={isAddMode ? 'ADD_PROFILE' : 'EDIT_PROFILE'} />
                <IconButton
                    onClick={onHide}
                    aria-label={Localize('CLOSE')}
                    sx={{ width: 44, height: 44, color: 'rgba(0, 40, 128, 0.6)' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ px: { xs: 2, md: 3 }, py: 1 }}>
                {/* Not `Name`: that key is "Prénom" in French because it labels a
                    person's first name elsewhere (GroupesView's `prenom` column).
                    A permission profile has a name, not a first name. */}
                <FormRow label={<Langs str="PROFILE_NAME" />}>
                    <TextField
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        placeholder={Localize('PROFILE_NAME')}
                        size="small"
                        fullWidth
                        autoComplete="off"
                        sx={fieldSx}
                    />
                </FormRow>

                <FormRow label={<Langs str="description" />}>
                    <TextField
                        value={formDescription}
                        onChange={(e) => setFormDescription(e.target.value)}
                        placeholder={Localize('description')}
                        size="small"
                        fullWidth
                        autoComplete="off"
                        sx={fieldSx}
                    />
                </FormRow>

                {!isAddMode && (
                    <FormRow label={<Langs str="active" />}>
                        {switchRow(
                            formActive,
                            setFormActive,
                            <Langs str={formActive ? 'active' : 'inactive'} />
                        )}
                    </FormRow>
                )}

                <FormRow label={<Langs str="PROFILE_DISPLAYABLE" />}>
                    {switchRow(
                        formDisplayable,
                        setFormDisplayable,
                        <Langs str={formDisplayable ? 'PROFILE_DISPLAYABLE' : 'PROFILE_HIDDEN'} />
                    )}
                    <Box component="small" className={pStyles.profileSmallText}>
                        <Langs str="PROFILE_DISPLAYABLE_HELP" />
                    </Box>
                </FormRow>

                <FormRow label={<IsManagerLabel />}>
                    {switchRow(
                        formIsManager,
                        setFormIsManager,
                        <Langs str={formIsManager ? 'YES' : 'NO'} />
                    )}
                </FormRow>

                <FormRow label={<Langs str="TEAMS" />}>
                    <Autocomplete
                        multiple
                        options={teamOptions || []}
                        value={selectedTeams}
                        onChange={(e, newValue) => setFormTeamIds(newValue.map(v => v.value))}
                        getOptionLabel={(option) => option.label ?? ''}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        size="small"
                        disableCloseOnSelect
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option.value}
                                    label={option.label}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255, 144, 21, 0.12)',
                                        color: '#c96f00',
                                        fontWeight: 600,
                                        height: 28,
                                    }}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={Localize('ALL_TEAMS')}
                                sx={fieldSx}
                            />
                        )}
                    />
                    <Box component="small" className={pStyles.profileSmallText}>
                        <Langs str="EMPTY_MEANS_ALL_TEAMS" />
                    </Box>
                </FormRow>

                {isAddMode && profileOptions && profileOptions.length > 0 && (
                    <FormRow label={<Langs str="INHERIT_PERMISSIONS" />}>
                        <Autocomplete
                            options={profileOptions}
                            value={selectedInherit}
                            onChange={(e, newValue) => setInheritFromProfileId(newValue ? newValue.id : null)}
                            getOptionLabel={(option) => option.name ?? ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            size="small"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={Localize('NONE')}
                                    sx={fieldSx}
                                />
                            )}
                        />
                        <Box component="small" className={pStyles.profileSmallText}>
                            <Langs str="INHERIT_PERMISSIONS_HELP" />
                        </Box>
                    </FormRow>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 2.5,
                    py: 2,
                    pb: isPhone ? 'calc(env(safe-area-inset-bottom, 0px) + 16px)' : 2,
                    gap: 2,
                    background: '#f8f9fc',
                    borderTop: '1px solid rgba(0, 40, 128, 0.08)',
                }}
            >
                <Button
                    onClick={onHide}
                    sx={{
                        minHeight: 48,
                        flex: { xs: 1, md: '0 0 auto' },
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        color: 'rgba(0, 40, 128, 0.7)',
                    }}
                >
                    <Langs str="cancel" />
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!formName.trim()}
                    className={pStyles.saveBtn}
                    sx={{ minHeight: 48, flex: { xs: 1, md: '0 0 auto' } }}
                >
                    <Langs str={isAddMode ? 'ADD' : 'update'} />
                </Button>
            </DialogActions>
        </Dialog>
    )
}
